import {
    formatSeriesData,
    getSeriesColors,
} from './Charts/MultiPredictionTimeSeriesChart/utils';
import { forwardRef, memo } from 'react';
import Chart from './Charts/MultiPredictionTimeSeriesChart';
import { CombinedTimeSeriesOverview } from 'components/TimeSeriesOverview';
import DriversOfGrowthTooltip from './Charts/MultiPredictionTimeSeriesChart/components/DriversOfGrowthTooltip';
import { TIKTOK_METADATA_BY_KEY } from 'utils/metrics';
import TikTokTimeSeriesNoDataMessage from 'components/TikTokTimeSeriesNoDataMessage';
import { Wrapper } from 'components/DataVis/TimeSeriesChart';
import { timeseries as copy } from '@/copy';
import dayjs from 'dayjs';
import { getAverageTimeSeries } from './utils/index';
import { getXAxisTickFns } from './Charts/TikTokSearchTimeSeries/utils';
import isEqual from 'react-fast-compare';
import styled from 'styled-components';
import weekOfYear from 'dayjs/plugin/weekOfYear';

dayjs.extend(weekOfYear);

const formatAndParseTime = (v) => {
    const s = dayjs(v, 'YYYY-MM-DD').format('MMM D, YYYY');
    return s;
};

const TikTokTooltip = forwardRef(
    (
        {
            formatAndParseTime: formatAndParseTimeProp = formatAndParseTime,
            metricLabel = 'views',
            sourceName = 'TikTok',
            ...props
        },
        ref,
    ) => (
        <DriversOfGrowthTooltip
            {...props}
            formatAndParseTime={formatAndParseTimeProp}
            metricLabel={metricLabel}
            sourceName={sourceName}
            ref={ref}
        />
    ),
);

const TikTokTimeSeriesWrapper = styled(Wrapper)`
    grid-template-columns: 1fr 72.1%;
    background: transparent;
`;

const defaultMetricsList = [
    TIKTOK_METADATA_BY_KEY.non_cum_YoY_rolling,
    TIKTOK_METADATA_BY_KEY.non_cum_QoQ_rolling,
    TIKTOK_METADATA_BY_KEY.non_cum_MoM_rolling,
    TIKTOK_METADATA_BY_KEY.non_cum_WoW,
];

const TikTokTimeSeries = memo(
    forwardRef(
        (
            {
                data,
                week,
                year,
                isLoading,
                noDataMessage = <TikTokTimeSeriesNoDataMessage />,
                Wrapper = TikTokTimeSeriesWrapper,
                Tooltip = TikTokTooltip,
                metricsList = defaultMetricsList,
                metrics,
                defaultMetric = TIKTOK_METADATA_BY_KEY.non_cum_YoY_rolling,
                viewSumTimeSeries,
                chartHeight = 250,
                showDataSourceBar = true,
                country,
                category,
                brand,
                ...props
            },
            ref,
        ) => {
            const sortedData =
                data && data.length
                    ? data
                          .slice()
                          .sort(
                              (a, b) =>
                                  b.timeSeries.length - a.timeSeries.length,
                          )
                    : data;
            // need to have longest timeseries first for correctly establishing xAxis labels
            const { xAxisTickFormat, xAxisTickValues } =
                getXAxisTickFns(sortedData);
            let maybeSumData = data;
            if (viewSumTimeSeries && data && data.length) {
                const { accessors } = formatSeriesData(data);
                maybeSumData = [
                    {
                        id: 'combined',
                        timeSeries: getAverageTimeSeries(data, accessors),
                        color: getSeriesColors(0),
                        name: 'Combined',
                    },
                ];
            }
            return (
                <div css="margin-bottom: 12px;" ref={ref}>
                    <Chart
                        chartHeight={chartHeight}
                        data={maybeSumData}
                        isLoading={isLoading}
                        showPlaceholder={!data || data.length < 1}
                        placeholderMessage={
                            isLoading ? copy.loadingTikTok : noDataMessage
                        }
                        predictionStrokeDasharray={false}
                        Wrapper={Wrapper}
                        xAxisTickFormat={xAxisTickFormat}
                        xAxisTickValues={xAxisTickValues}
                        Tooltip={Tooltip}
                        country={country}
                        category={category}
                        brand={brand}
                    >
                        <CombinedTimeSeriesOverview
                            isLoading={isLoading}
                            metrics={
                                metrics
                                    ? [
                                          {
                                              metrics: metrics || {},
                                              metricsList,
                                              defaultMetric,
                                              week,
                                              year,
                                          },
                                      ]
                                    : null
                            }
                            showDataSource={false}
                            showDataSourceBar={showDataSourceBar}
                        />
                    </Chart>
                </div>
            );
        },
    ),
    isEqual,
);

export default TikTokTimeSeries;
